import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errorHandler'
import { convertTo24HourFormat, convertTo24HourParts } from '../utils/timezoneUtils'
import * as Sentry from '@sentry/react'

/**
 * Send course schedule change notification emails to all enrolled students
 * Called when an instructor modifies the course schedule (time, duration, days, etc.)
 * which affects all future sessions
 * @param {Object} data - Course schedule change data
 * @returns {Promise<void>}
 */
async function sendCourseScheduleChangeEmails(data) {
  try {
    // Fetch all enrolled students' emails for this course
    const { data: enrollments, error: enrollmentsError } = await supabase
      .from('enrollments')
      .select(`
        student_id,
        profiles!student_id(email)
      `)
      .eq('course_id', data.courseId)
      .eq('status', 'active')

    if (enrollmentsError) {
      console.error('Error fetching enrollments for course schedule change notification:', enrollmentsError)
      return
    }

    const studentEmails = enrollments
      ?.map(e => e.profiles?.email)
      .filter(email => email) || []

    if (studentEmails.length === 0) {
      console.log('No enrolled students to notify about course schedule change')
      return
    }

    // Fetch the next upcoming session to use as the reference for the email
    const { data: nextSessions, error: sessionsError } = await supabase
      .from('sessions')
      .select('id, title, session_date, duration_minutes')
      .eq('course_id', data.courseId)
      .gte('session_date', new Date().toISOString())
      .order('session_date', { ascending: true })
      .limit(1)

    if (sessionsError || !nextSessions || nextSessions.length === 0) {
      console.log('No upcoming sessions found to reference in notification email')
      return
    }

    const nextSession = nextSessions[0]

    // Get auth token for edge function call
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token

    const payload = {
      sessionId: nextSession.id,
      sessionTitle: `All future sessions (next: ${nextSession.title})`,
      courseId: data.courseId,
      courseTitle: data.courseTitle,
      instructorName: data.instructorName,
      instructorEmail: data.instructorEmail,
      meetingLink: data.meetingLink,
      timezone: data.timezone,
      oldSessionDate: data.oldNextSessionDate || nextSession.session_date,
      newSessionDate: nextSession.session_date,
      newDurationMinutes: nextSession.duration_minutes || data.newDuration || 60,
      studentEmails
    }

    console.log(`Sending course schedule change notification to ${studentEmails.length} students`)

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-session-updated-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(payload)
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(`Email function error: ${JSON.stringify(error)}`)
    }

    const result = await response.json()
    console.log('Course schedule change emails sent successfully:', result)
  } catch (error) {
    console.error('Error sending course schedule change emails:', error)
    // Non-blocking - don't throw
  }
}

/**
 * Course Service - Backend API functions for course management
 * Handles all course-related database operations with proper error handling
 */

/**
 * Create a new course with sessions (if applicable)
 * @param {Object} courseData - Course information
 * @param {Object} user - Current authenticated user
 * @returns {Promise<Object>} Created course with sessions
 */
export async function createCourse(courseData, user) {
  if (!user) {
    throw new Error('Authentication required to create a course')
  }

  // Validate required fields
  const requiredFields = ['title', 'description', 'category', 'courseType']
  for (const field of requiredFields) {
    if (!courseData[field]) {
      throw new Error(`${field} is required`)
    }
  }

  try {
    // Prepare course data for database
    const dbCourseData = {
      title: courseData.title.trim(),
      description: courseData.description.trim(),
      category: courseData.category,
      age_group: courseData.ageGroup || 'All ages', // Default to 'All ages' if not provided
      course_type: courseData.courseType,
      price: 0, // Free for now
      session_duration: courseData.sessionDuration ? parseInt(courseData.sessionDuration) : null,
      enrollment_limit: courseData.enrollmentLimit ? parseInt(courseData.enrollmentLimit) : null,
      is_self_paced: courseData.isSelfPaced || false,
      instructor_id: user.id,
      status: 'published', // Auto-publish new courses
      is_published: true,
      timezone: courseData.timezone || 'America/New_York', // Store instructor's timezone
      meeting_link: courseData.meetingLink || null, // Store course-level meeting link
      // Add missing date and schedule fields
      start_date: courseData.startDate || null,
      end_date: courseData.endDate || null,
      has_end_date: courseData.hasEndDate || false,
      session_time: convertTo24HourFormat(courseData.sessionTime),
      selected_days: courseData.selectedDays || null,
      frequency: courseData.frequency || 'weekly'
    }

    // Create the course
    const { data: course, error: courseError } = await supabase
      .from('courses')
      .insert([dbCourseData])
      .select()
      .single()

    if (courseError) {
      console.error('Course creation error:', courseError)
      throw new Error(`Failed to create course: ${courseError.message}`)
    }

    // Create sessions for group courses with schedules
    let sessions = []
    if (courseData.courseType === 'group' && 
        !courseData.isSelfPaced && 
        courseData.startDate) {
      
      // For 'never' frequency, we can create sessions even without selected days
      // For recurring frequencies, we need selected days
      const canCreateSessions = courseData.frequency === 'never' || courseData.selectedDays?.length > 0
      
      if (canCreateSessions) {
        sessions = await createCourseSessions(course.id, courseData)
      }
    }

    return {
      course,
      sessions,
      message: 'Course created and published successfully! Students can now enroll.'
    }

  } catch (error) {
    console.error('Error in createCourse:', error)
    throw error
  }
}

/**
 * Create sessions for a group course
 * @param {string} courseId - Course ID
 * @param {Object} scheduleData - Schedule information
 * @returns {Promise<Array>} Created sessions
 */
async function createCourseSessions(courseId, scheduleData) {
  const sessions = []
  const startDate = new Date(scheduleData.startDate)
  const frequency = scheduleData.frequency || 'weekly'
  
  // Validate required fields
  if (!scheduleData.sessionTime) {
    console.error('Session time is required to create sessions')
    return []
  }
  
  if (!scheduleData.sessionDuration) {
    console.error('Session duration is required to create sessions')
    return []
  }
  
  // Handle one-time events (frequency = 'never')
  // Create one session for each selected day starting from start date
  if (frequency === 'never') {
    const selectedDays = scheduleData.selectedDays || []
    
    if (selectedDays.length === 0) {
      // If no days selected, create one session on start date
      const sessionDateTime = new Date(startDate)
      const { hours, minutes } = convertTo24HourParts(scheduleData.sessionTime)
      sessionDateTime.setHours(hours, minutes, 0, 0)

      sessions.push({
        course_id: courseId,
        title: 'Session Topics',
        description: '',
        session_date: sessionDateTime.toISOString(),
        duration_minutes: parseInt(scheduleData.sessionDuration),
        status: 'scheduled'
      })
    } else {
      // Create one session for each selected day
      let currentDate = new Date(startDate)
      let sessionNumber = 1
      const maxDaysToCheck = 7 // Check up to 7 days from start date
      let daysChecked = 0
      
      while (sessions.length < selectedDays.length && daysChecked < maxDaysToCheck) {
        const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' })
        
        if (selectedDays.includes(dayName)) {
          const sessionDateTime = new Date(currentDate)
          const { hours, minutes } = convertTo24HourParts(scheduleData.sessionTime)
          sessionDateTime.setHours(hours, minutes, 0, 0)

          sessions.push({
            course_id: courseId,
            title: `Session ${sessionNumber} Topics`,
            description: '',
            session_date: sessionDateTime.toISOString(),
            duration_minutes: parseInt(scheduleData.sessionDuration),
            status: 'scheduled'
          })
          
          sessionNumber++
        }
        
        currentDate.setDate(currentDate.getDate() + 1)
        daysChecked++
      }
    }

    // Insert the sessions
    if (sessions.length > 0) {
      const { data: createdSessions, error: sessionsError } = await supabase
        .from('sessions')
        .insert(sessions)
        .select()

      if (sessionsError) {
        console.error('Error creating sessions:', sessionsError)
        return []
      }

      console.log(`Created ${createdSessions.length} one-time session(s) for course ${courseId}`)
      return createdSessions
    }

    return []
  }
  
  // For recurring courses (weekly or bi-weekly)
  const hasEndDate = scheduleData.hasEndDate && scheduleData.endDate
  const endDate = hasEndDate
    ? new Date(scheduleData.endDate)
    : null
  
  const maxSessions = hasEndDate ? 1000 : 5 // Limit to 5 for open-ended courses

  let currentDate = new Date(startDate)
  let sessionNumber = 1
  let sessionsCreated = 0
  let weekCount = 0
  let lastWeekStart = new Date(startDate)
  lastWeekStart.setDate(lastWeekStart.getDate() - lastWeekStart.getDay()) // Get Sunday of start week

  while (sessionsCreated < maxSessions && (!endDate || currentDate <= endDate)) {
    const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' })
    
    // For bi-weekly, check if we're in an active week
    const currentWeekStart = new Date(currentDate)
    currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay())
    const weeksSinceStart = Math.floor((currentWeekStart - lastWeekStart) / (7 * 24 * 60 * 60 * 1000))
    const isActiveWeek = frequency === 'weekly' || (frequency === 'biweekly' && weeksSinceStart % 2 === 0)
    
    if (isActiveWeek && scheduleData.selectedDays.includes(dayName)) {
      const sessionDateTime = new Date(currentDate)
      const { hours, minutes } = convertTo24HourParts(scheduleData.sessionTime)
      sessionDateTime.setHours(hours, minutes, 0, 0)

      sessions.push({
        course_id: courseId,
        title: `Session ${sessionNumber} Topics`,
        description: '',
        session_date: sessionDateTime.toISOString(),
        duration_minutes: parseInt(scheduleData.sessionDuration),
        status: 'scheduled'
      })
      
      sessionNumber++
      sessionsCreated++
    }
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1)
  }

  // Insert sessions if any were created
  if (sessions.length > 0) {
    const { data: createdSessions, error: sessionsError } = await supabase
      .from('sessions')
      .insert(sessions)
      .select()

    if (sessionsError) {
      console.error('Error creating sessions:', sessionsError)
      // Don't fail the whole process, just log the error
      return []
    }

    console.log(`Created ${createdSessions.length} sessions for course ${courseId}`)
    return createdSessions
  }

  return []
}

/**
 * Get courses for an instructor
 * @param {string} instructorId - Instructor user ID
 * @returns {Promise<Array>} List of instructor's courses with timeline info
 */
export async function getInstructorCourses(instructorId) {
  if (!instructorId) {
    throw new Error('Instructor ID is required')
  }

  try {
    const { data: courses, error } = await supabase
      .from('courses')
      .select(`
        *,
        enrollments!inner(count),
        sessions(count)
      `)
      .eq('instructor_id', instructorId)
      .neq('enrollments.status', 'dropped')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching instructor courses:', error)
      throw new Error(`Failed to fetch courses: ${error.message}`)
    }

    return courses || []
  } catch (error) {
    console.error('Error in getInstructorCourses:', error)
    throw error
  }
}

/**
 * Get all published courses (for marketplace)
 * @returns {Promise<Array>} List of published courses
 */
export async function getAllPublishedCourses() {
  try {
    // Add breadcrumb for debugging
    Sentry.addBreadcrumb({
      category: 'api',
      message: 'Fetching published courses',
      level: 'info',
    });

    const { data: courses, error } = await supabase
      .from('courses')
      .select(`
        *,
        profiles!instructor_id(id, first_name, last_name, avatar_url),
        enrollments!left(count),
        sessions(count)
      `)
      .eq('status', 'published')
      .eq('is_published', true)
      .not('enrollments.status', 'eq', 'dropped')
      .order('created_at', { ascending: false })

    if (error) {
      throw handleSupabaseError(error, {
        operation: 'getAllPublishedCourses',
        table: 'courses',
      });
    }

    // Sort by display_order client-side (works whether or not the column exists)
    if (courses && courses.length > 0 && courses[0].display_order !== undefined) {
      courses.sort((a, b) => {
        const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER
        const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) return orderA - orderB
        return new Date(b.created_at) - new Date(a.created_at)
      })
    }

    // Transform the data to match expected format
    const transformedCourses = (courses || []).map(course => ({
      ...course,
      instructorName: course.profiles 
        ? `${course.profiles.first_name} ${course.profiles.last_name}`.trim()
        : 'Unknown Instructor',
      currentEnrollment: course.enrollments?.[0]?.count || 0,
      totalSessions: course.sessions?.[0]?.count || 0,
      sessionFrequency: course.frequency || 'weekly' // Add default frequency
    }))

    // Fetch ratings for all courses
    const coursesWithRatings = await Promise.all(
      transformedCourses.map(async (course) => {
        try {
          const { data: reviews } = await supabase
            .from('reviews')
            .select('rating')
            .eq('course_id', course.id)
            .eq('is_published', true)

          const totalRatings = reviews?.length || 0
          const averageRating = totalRatings > 0
            ? Math.round((reviews.reduce((sum, r) => sum + r.rating, 0) / totalRatings) * 10) / 10
            : 0

          return {
            ...course,
            averageRating,
            totalRatings
          }
        } catch (error) {
          console.error(`Error fetching ratings for course ${course.id}:`, error)
          return {
            ...course,
            averageRating: 0,
            totalRatings: 0
          }
        }
      })
    )

    return coursesWithRatings
  } catch (error) {
    console.error('Error in getAllPublishedCourses:', error)
    throw error
  }
}

/**
 * Get a single course by ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} Course details
 */
export async function getCourseById(courseId) {
  if (!courseId) {
    throw new Error('Course ID is required')
  }

  try {
    const { data: course, error } = await supabase
      .from('courses')
      .select(`
        *,
        profiles!instructor_id(id, first_name, last_name, avatar_url, bio, expertise),
        enrollments!left(count),
        sessions(*)
      `)
      .eq('id', courseId)
      .not('enrollments.status', 'eq', 'dropped')
      .single()

    if (error) {
      console.error('Error fetching course:', error)
      throw new Error(`Failed to fetch course: ${error.message}`)
    }

    // Fetch ratings for this course
    const { data: reviews } = await supabase
      .from('reviews')
      .select('rating')
      .eq('course_id', courseId)
      .eq('is_published', true)

    const totalRatings = reviews?.length || 0
    const averageRating = totalRatings > 0
      ? Math.round((reviews.reduce((sum, r) => sum + r.rating, 0) / totalRatings) * 10) / 10
      : 0

    // Transform the data to match expected format
    const transformedCourse = {
      ...course,
      instructorName: course.profiles 
        ? `${course.profiles.first_name} ${course.profiles.last_name}`.trim()
        : 'Unknown Instructor',
      instructorBio: course.profiles?.bio || '',
      instructorExpertise: course.profiles?.expertise || [],
      currentEnrollment: course.enrollments?.[0]?.count || 0,
      totalSessions: course.sessions?.length || 0,
      averageRating,
      totalRatings,
      sessionFrequency: course.frequency || 'weekly',
      selectedDays: course.selected_days || [],
      dayTimes: course.session_time ? [course.session_time] : [],
      isSelfPaced: course.is_self_paced || false,
      sessionDuration: course.session_duration || 60,
      enrollmentLimit: course.enrollment_limit || null,
      price: course.price || 0
    }

    return transformedCourse
  } catch (error) {
    console.error('Error in getCourseById:', error)
    throw error
  }
}

/**
 * Update course information
 * @param {string} courseId - Course ID
 * @param {Object} updates - Fields to update
 * @param {string} instructorId - Instructor user ID (for authorization)
 * @returns {Promise<Object>} Updated course
 */
export async function updateCourse(courseId, updates, instructorId) {
  if (!courseId || !instructorId) {
    throw new Error('Course ID and instructor ID are required')
  }

  try {
    // Verify instructor owns the course and fetch current schedule data + instructor info
    const { data: course, error: fetchError } = await supabase
      .from('courses')
      .select(`
        instructor_id, title, session_time, session_duration, start_date, end_date, 
        selected_days, frequency, meeting_link, timezone,
        profiles!instructor_id(full_name, email)
      `)
      .eq('id', courseId)
      .single()

    if (fetchError) {
      throw new Error(`Course not found: ${fetchError.message}`)
    }

    if (course.instructor_id !== instructorId) {
      throw new Error('Unauthorized: You can only update your own courses')
    }

    // Check if any schedule-related fields are being updated
    const isTimeUpdated = updates.session_time && updates.session_time !== course.session_time
    const isDurationUpdated = updates.session_duration && updates.session_duration !== course.session_duration
    const isStartDateUpdated = updates.start_date && updates.start_date !== course.start_date
    const isEndDateUpdated = 'end_date' in updates && updates.end_date !== course.end_date
    const isDaysUpdated = updates.selected_days && JSON.stringify(updates.selected_days) !== JSON.stringify(course.selected_days)
    const isFrequencyUpdated = updates.frequency && updates.frequency !== course.frequency

    // Determine if we need to regenerate all sessions (major schedule change)
    const needsSessionRegeneration = isStartDateUpdated || isEndDateUpdated || isDaysUpdated || isFrequencyUpdated
    
    // Determine if we just need to update existing sessions (minor schedule change)
    const needsSessionUpdate = !needsSessionRegeneration && (isTimeUpdated || isDurationUpdated)

    // Track whether we need to send schedule change notifications
    const scheduleHasChanged = needsSessionRegeneration || needsSessionUpdate

    // Capture the old next session date before making changes (for the notification email)
    let oldNextSessionDate = null
    if (scheduleHasChanged) {
      const { data: oldNextSessions } = await supabase
        .from('sessions')
        .select('session_date')
        .eq('course_id', courseId)
        .gte('session_date', new Date().toISOString())
        .order('session_date', { ascending: true })
        .limit(1)
      
      if (oldNextSessions && oldNextSessions.length > 0) {
        oldNextSessionDate = oldNextSessions[0].session_date
      }
    }

    // Prepare update data
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString()
    }

    // Update the course
    const { data: updatedCourse, error: updateError } = await supabase
      .from('courses')
      .update(updateData)
      .eq('id', courseId)
      .select()
      .single()

    if (updateError) {
      console.error('Error updating course:', updateError)
      throw new Error(`Failed to update course: ${updateError.message}`)
    }

    // Handle session updates based on what changed
    if (needsSessionRegeneration) {
      // Major schedule change - delete all future sessions and regenerate
      const now = new Date().toISOString()
      
      // Delete all future sessions
      const { error: deleteError } = await supabase
        .from('sessions')
        .delete()
        .eq('course_id', courseId)
        .gte('session_date', now)

      if (deleteError) {
        console.error('Error deleting future sessions:', deleteError)
      } else {
        // Regenerate sessions with new schedule
        const scheduleData = {
          startDate: updates.start_date || course.start_date,
          endDate: updates.end_date !== undefined ? updates.end_date : course.end_date,
          hasEndDate: updates.end_date !== undefined ? !!updates.end_date : !!course.end_date,
          selectedDays: updates.selected_days || course.selected_days,
          sessionTime: updates.session_time || course.session_time,
          sessionDuration: updates.session_duration || course.session_duration,
          meetingLink: updates.meeting_link || updatedCourse.meeting_link,
          frequency: updates.frequency || course.frequency
        }

        if (scheduleData.startDate && scheduleData.selectedDays?.length > 0) {
          const newSessions = await createCourseSessions(courseId, scheduleData)
          console.log(`Regenerated ${newSessions.length} sessions with new schedule`)
        }
      }
    } else if (needsSessionUpdate) {
      // Minor schedule change - just update existing future sessions
      const now = new Date().toISOString()
      
      // Fetch all future sessions
      const { data: futureSessions, error: fetchError } = await supabase
        .from('sessions')
        .select('*')
        .eq('course_id', courseId)
        .gte('session_date', now)

      if (fetchError) {
        console.error('Error fetching sessions:', fetchError)
      } else if (futureSessions && futureSessions.length > 0) {
        // Update each session's date/time and duration
        const updatePromises = futureSessions.map(session => {
          const sessionDate = new Date(session.session_date)
          
          // If time was updated, apply new time to the session date
          if (isTimeUpdated) {
            const [hours, minutes] = updates.session_time.split(':')
            sessionDate.setHours(parseInt(hours), parseInt(minutes), 0, 0)
          }
          
          const sessionUpdates = {
            session_date: sessionDate.toISOString(),
            updated_at: new Date().toISOString()
          }
          
          // If duration was updated, apply new duration
          if (isDurationUpdated) {
            sessionUpdates.duration_minutes = updates.session_duration
          }
          
          return supabase
            .from('sessions')
            .update(sessionUpdates)
            .eq('id', session.id)
        })

        const results = await Promise.all(updatePromises)
        const errors = results.filter(r => r.error)
        
        if (errors.length > 0) {
          console.error('Some sessions failed to update:', errors)
        } else {
          console.log(`Updated ${futureSessions.length} future sessions`)
        }
      }
    }

    // Send schedule change notification emails to all enrolled students (non-blocking)
    if (scheduleHasChanged) {
      sendCourseScheduleChangeEmails({
        courseId,
        courseTitle: updates.title || course.title,
        instructorName: course.profiles?.full_name || 'Instructor',
        instructorEmail: course.profiles?.email,
        meetingLink: updates.meeting_link || course.meeting_link,
        timezone: updates.timezone || course.timezone || 'UTC',
        oldNextSessionDate,
        newDuration: updates.session_duration || course.session_duration
      }).catch(error => {
        console.error('Failed to send course schedule change notification emails:', error)
      })
    }

    return updatedCourse
  } catch (error) {
    console.error('Error in updateCourse:', error)
    throw error
  }
}

/**
 * Publish a course (make it visible to students)
 * @param {string} courseId - Course ID
 * @param {string} instructorId - Instructor user ID
 * @returns {Promise<Object>} Updated course
 */
export async function publishCourse(courseId, instructorId) {
  return updateCourse(courseId, { 
    is_published: true, 
    status: 'published' 
  }, instructorId)
}

/**
 * Unpublish a course (hide from students)
 * @param {string} courseId - Course ID
 * @param {string} instructorId - Instructor user ID
 * @returns {Promise<Object>} Updated course
 */
export async function unpublishCourse(courseId, instructorId) {
  return updateCourse(courseId, { 
    is_published: false, 
    status: 'draft' 
  }, instructorId)
}

/**
 * Delete a course and all related data
 * @param {string} courseId - Course ID
 * @param {string} instructorId - Instructor user ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteCourse(courseId, instructorId) {
  if (!courseId || !instructorId) {
    throw new Error('Course ID and instructor ID are required')
  }

  try {
    // Verify instructor owns the course
    const { data: course, error: fetchError } = await supabase
      .from('courses')
      .select('instructor_id, title')
      .eq('id', courseId)
      .single()

    if (fetchError) {
      throw new Error(`Course not found: ${fetchError.message}`)
    }

    if (course.instructor_id !== instructorId) {
      throw new Error('Unauthorized: You can only delete your own courses')
    }

    // Check if course has active enrollments (exclude dropped)
    const { data: enrollments, error: enrollmentError } = await supabase
      .from('enrollments')
      .select('id')
      .eq('course_id', courseId)
      .neq('status', 'dropped') // Exclude dropped enrollments
      .limit(1)

    if (enrollmentError) {
      console.error('Error checking enrollments:', enrollmentError)
    }

    if (enrollments && enrollments.length > 0) {
      throw new Error('Cannot delete course with active enrollments. Please contact students first.')
    }

    // Delete the course (CASCADE will handle related data)
    const { error: deleteError } = await supabase
      .from('courses')
      .delete()
      .eq('id', courseId)

    if (deleteError) {
      console.error('Error deleting course:', deleteError)
      throw new Error(`Failed to delete course: ${deleteError.message}`)
    }

    return true
  } catch (error) {
    console.error('Error in deleteCourse:', error)
    throw error
  }
}

/**
 * Get course statistics for instructor dashboard
 * @param {string} instructorId - Instructor user ID
 * @returns {Promise<Object>} Course statistics
 */
export async function getCourseStats(instructorId) {
  if (!instructorId) {
    throw new Error('Instructor ID is required')
  }

  try {
    // Get course counts
    const { data: courses, error: coursesError } = await supabase
      .from('courses')
      .select('id, status, is_published')
      .eq('instructor_id', instructorId)

    if (coursesError) {
      throw new Error(`Failed to fetch course stats: ${coursesError.message}`)
    }

    // Get enrollment counts
    const { data: enrollments, error: enrollmentsError } = await supabase
      .from('enrollments')
      .select('course_id')
      .in('course_id', courses.map(c => c.id))

    if (enrollmentsError) {
      console.error('Error fetching enrollment stats:', enrollmentsError)
    }

    const stats = {
      totalCourses: courses.length,
      publishedCourses: courses.filter(c => c.is_published).length,
      draftCourses: courses.filter(c => !c.is_published).length,
      totalEnrollments: enrollments ? enrollments.length : 0
    }

    return stats
  } catch (error) {
    console.error('Error in getCourseStats:', error)
    throw error
  }
}